/**
 * Listes d'options pour les filtres de la recherche établissements (aligné ancien site).
 * Les `slug` servent dans l'URL (`type=a,b`, `domain=…`, etc.) — sans virgules dans les libellés.
 */

import type {
  EstablishmentFilterOption,
  EstablishmentLevelSection,
} from '~/types/establishmentSearchFilterLists'

export type {
  EstablishmentFilterOption,
  EstablishmentLevelSection,
} from '~/types/establishmentSearchFilterLists'
export const ESTABLISHMENT_SCHOOL_TYPE_OPTIONS: readonly EstablishmentFilterOption[] = [
  { slug: 'cfa', label: 'CFA' },
  { slug: 'ecole-informatique', label: "École d'informatique" },
  { slug: 'ecole-ingenieurs', label: "École d'ingénieurs" },
  { slug: 'ecole-agronomie', label: "École d'Agronomie, d'Agriculture et Environnement" },
  { slug: 'ecole-architecture', label: "École d'Architecture" },
  { slug: 'ecole-gestion-commerce', label: 'École de Gestion et de Commerce' },
  { slug: 'ecole-sante-social', label: 'École de Santé et du Secteur Social' },
  { slug: 'ecole-media-culture', label: 'École Média, Culture et Communication' },
  { slug: 'enm', label: 'École nationale de la magistrature (ENM)' },
  { slug: 'env', label: 'École Nationale de Vétérinaire (ENV)' },
  { slug: 'ecole-sup-art', label: "École Supérieure d'Art" },
  { slug: 'iep', label: 'École de Sciences Politiques (IEP)' },
  { slug: 'ecole-web-digital', label: 'École du Web et du digital' },
  { slug: 'manaa-dnmade', label: 'MANAA & DNMADE' },
  { slug: 'universite', label: 'Université' },
  { slug: 'autre', label: 'Autre' },
  { slug: 'classes-prepas', label: 'Classes préparatoires' },
  { slug: 'cned-distance', label: "CNED et établissement d'enseignement à distance" },
  { slug: 'ecole-esthetique', label: "École d'esthétique" },
  { slug: 'ecole-immobilier', label: "École d'immobilier" },
  { slug: 'ecole-dietetique', label: 'École de diététique' },
  { slug: 'ecole-fonction-publique-armee', label: 'École de la fonction publique et armée' },
  { slug: 'ecole-management', label: 'École de Management' },
  { slug: 'ecole-marketing', label: 'Ecole de Marketing' },
  { slug: 'iae', label: 'IAE' },
  { slug: 'ecole-en-ligne', label: 'École en ligne' },
  { slug: 'inspe', label: 'INSPE' },
  { slug: 'iut', label: 'IUT' },
  { slug: 'sts', label: 'STS' },
  { slug: 'ecole-mode', label: 'École de Mode' },
  { slug: 'ecole-luxe', label: 'École du Luxe' },
  { slug: 'ecole-culinaire', label: 'École Culinaire' },
  { slug: 'ecole-design', label: 'École Supérieure de Design' },
  { slug: 'ecole-transition', label: 'École de transition' },
  { slug: 'ecole-innovation', label: "École d'innovation" },
  { slug: 'eplefpa', label: 'EPLEFPA' },
  { slug: 'ecole-droit', label: 'École de Droit' },
  { slug: 'ecole-tourisme', label: 'École de Tourisme' },
] as const

/** Types de formation (multi, surtout page formations). */
export const ESTABLISHMENT_FORMATION_TYPE_OPTIONS: readonly EstablishmentFilterOption[] = [
  { slug: 'initiale', label: 'Formation initiale' },
  { slug: 'apprentissage', label: "Alternance : Contrat d'apprentissage" },
  { slug: 'professionnalisation', label: 'Alternance : Contrat de professionnalisation' },
] as const

/** Niveaux / diplômes (multi), groupés comme l'ancien site. */
export const ESTABLISHMENT_LEVEL_SECTIONS: readonly EstablishmentLevelSection[] = [
  {
    sectionSlug: 'avant-bac',
    sectionLabel: 'Niveau avant Bac',
    options: [
      { slug: 'cap-equiv', label: 'CAP ou équivalent' },
      { slug: 'diplome-etat', label: "Diplôme d'État" },
      { slug: 'mention-complementaire-av', label: 'Mention Complémentaire' },
      { slug: 'titre-certifie', label: 'Titre certifié' },
      { slug: 'titre-pro', label: 'Titre professionnel' },
    ],
  },
  {
    sectionSlug: 'bac',
    sectionLabel: 'Niveau Bac',
    options: [
      { slug: 'brevet-pro', label: 'Brevet Professionel' },
      { slug: 'cap-bac', label: "Certificat d'Aptitude Professionnelle (CAP)" },
      { slug: 'mention-complementaire-bac', label: 'Mention complémentaire (Niveau Bac)' },
    ],
  },
  {
    sectionSlug: 'bac-1-2',
    sectionLabel: 'Niveau Bac+1 à Bac+2',
    options: [
      { slug: 'bts', label: 'Brevet Technicien Supérieur (BTS)' },
      { slug: 'cpge', label: 'Classes préparatoires' },
      { slug: 'dut', label: 'Diplôme Universitaire de Technologie (DUT)' },
    ],
  },
  {
    sectionSlug: 'bac-3',
    sectionLabel: 'Niveau Bac+3',
    options: [
      { slug: 'bachelor', label: 'Bachelor' },
      { slug: 'but', label: 'Bachelor universitaire de technologie (BUT)' },
      { slug: 'diplome-etat-b3', label: "Diplôme d'Etat" },
      { slug: 'dcg', label: 'Diplôme de Comptabilité et de Gestion (DCG)' },
      { slug: 'dees', label: "Diplôme européen d'études supérieures(DEES)" },
      { slug: 'licence', label: 'Licence' },
      { slug: 'licence-pro', label: 'Licence professionnelle' },
    ],
  },
  {
    sectionSlug: 'bac-4-5',
    sectionLabel: 'Niveau Bac+4 à Bac+5',
    options: [
      { slug: 'bba', label: 'Bachelor (BBA)' },
      { slug: 'ingenieur', label: "Diplôme d'ingénieur" },
      { slug: 'dscg', label: 'Diplômes supérieurs de comptabilité et gestion (DSCG)' },
      { slug: 'master', label: 'Master' },
      { slug: 'msc', label: 'Master of Science (MSc)' },
    ],
  },
  {
    sectionSlug: 'bac-6-plus',
    sectionLabel: 'Niveau Bac+6 et plus',
    options: [
      { slug: 'agregation', label: 'Agrégation' },
      { slug: 'doctorat', label: 'Doctorat' },
      { slug: 'mba', label: 'Master of Business Administration (MBA)' },
      { slug: 'ms', label: 'Mastère spécialisé (MS)' },
    ],
  },
] as const

/** Domaines « Nos domaines » (ancien site). */
export const ESTABLISHMENT_DOMAIN_OPTIONS: readonly EstablishmentFilterOption[] = [
  { slug: 'agriculture', label: 'Agriculture, Alimentation, Environnement, Energie' },
  { slug: 'archi-interieur', label: 'Architecture intérieur design' },
  { slug: 'archi-urbanisme', label: 'Architecture, Urbanisme' },
  { slug: 'art-culture', label: 'Art, Culture, Design, Mode' },
  { slug: 'artisanat', label: 'Artisanat' },
  { slug: 'btp', label: 'BTP, Bâtiment, Construction' },
  { slug: 'cinema', label: 'Cinéma, Audiovisuel, Sons' },
  { slug: 'commerce', label: 'Commerce, Management, RH' },
  { slug: 'droit', label: 'Droit, Sciences Politiques' },
  { slug: 'enseignement', label: 'Enseignement, Fonction publique' },
  { slug: 'finance', label: 'Finance, Banque, Comptabilité' },
  { slug: 'fonction-publique', label: 'Fonction publique, Armée, Sécurité, Défense' },
  { slug: 'hotellerie', label: 'Hôtellerie, Restauration, Tourisme' },
  { slug: 'immobilier', label: 'Immobilier' },
  { slug: 'industrie', label: 'Industrie, Chimie' },
  { slug: 'ingenierie', label: 'Ingénierie, Mécanique' },
  { slug: 'innovation', label: 'Innovation, Entrepreneuriat' },
  { slug: 'langues', label: 'Langues, Sciences Humaines, Lettres' },
  { slug: 'marketing', label: 'Marketing, Communication, Edition, Journalisme' },
  { slug: 'bouche', label: 'Métiers de bouche' },
  { slug: 'numerique', label: 'Numérique, Digital, Informatique, Programmation' },
  { slug: 'orientation', label: 'Orientation et renforcement' },
  { slug: 'sante', label: 'Santé, Paramédical, Social, Aide à la personne' },
  { slug: 'sport', label: 'Sport, Bien-être, Soins' },
  { slug: 'transport', label: 'Transport, Logistique, Distribution' },
] as const

/**
 * @param {string} slug - Identifiant slug type école.
 * @returns {EstablishmentFilterOption | undefined} Option type école par slug.
 */
export function findSchoolTypeOption(slug: string): EstablishmentFilterOption | undefined {
  return ESTABLISHMENT_SCHOOL_TYPE_OPTIONS.find((o: EstablishmentFilterOption) => o.slug === slug)
}

/**
 * @param {string} slug - Identifiant slug domaine.
 * @returns {EstablishmentFilterOption | undefined} Option domaine par slug.
 */
export function findDomainOption(slug: string): EstablishmentFilterOption | undefined {
  return ESTABLISHMENT_DOMAIN_OPTIONS.find((o: EstablishmentFilterOption) => o.slug === slug)
}

/**
 * @param {string} slug - Identifiant slug niveau.
 * @returns {EstablishmentFilterOption | undefined} Option niveau par slug.
 */
export function findLevelOption(slug: string): EstablishmentFilterOption | undefined {
  for (const sec of ESTABLISHMENT_LEVEL_SECTIONS) {
    const found: EstablishmentFilterOption | undefined = sec.options.find(
      (o: EstablishmentFilterOption) => o.slug === slug,
    )
    if (found) return found
  }
  return undefined
}

/**
 * @param {string} slug - Identifiant slug type de formation.
 * @returns {EstablishmentFilterOption | undefined} Option type formation par slug.
 */
export function findFormationTypeOption(slug: string): EstablishmentFilterOption | undefined {
  return ESTABLISHMENT_FORMATION_TYPE_OPTIONS.find(
    (o: EstablishmentFilterOption) => o.slug === slug,
  )
}
